import _ from 'lodash';
import { SystemRole } from '../auth/systemRole';
import { ICurrentUser } from '../currentuser/currentUser';

export enum AppRoute {
  home = '/',
  signIn = '/signin',
  signUp = '/signup',
  signOut = '/sign-out',
  settings = '/settings',
  settingsTeamBilling = '/settings/team/billing',
  settingsTeamUsers = '/settings/team/members',
  settingsTeamProgramTypes = '/settings/team/program-types',
  settingsTeamIntegrations = '/settings/team/integrations',
  settingsPersonalProfile = '/settings/personal/profile',
  settingsPersonalIntegrations = '/settings/personal/integrations',
  settingsPersonalNotifications = '/settings/personal/notifications',
  notFound = '/not-found',
  calendar = '/calendar',
  businessPlan = '/business-plan',
  marketplace = '/marketplace',
  deeplink = '/program/:programId',
  insights = '/insights',
  billing = '/billing',
  ai = '/ai',
  funding = '/funding',
}

export const AppRoutes = {
  Home: AppRoute.home,
  Calendar: AppRoute.calendar,
  BusinessPlan: AppRoute.businessPlan,
  Marketplace: AppRoute.marketplace,
  NotFound: AppRoute.notFound,
  Settings: {
    root: AppRoute.settings,
    team: {
      billing: AppRoute.settingsTeamBilling,
      users: AppRoute.settingsTeamUsers,
      programTypes: AppRoute.settingsTeamProgramTypes,
      integrations: AppRoute.settingsTeamIntegrations,
    },
    personal: {
      profile: AppRoute.settingsPersonalProfile,
      integrations: AppRoute.settingsPersonalIntegrations,
      notifications: AppRoute.settingsPersonalNotifications,
    },
  },
  Auth: {
    signIn: AppRoute.signIn,
    signUp: AppRoute.signUp,
    signOut: AppRoute.signOut,
  },
  Program: {
    deeplink: AppRoute.deeplink,
  },
  Recommendations: AppRoute.insights,
  GrowegyAI: AppRoute.ai,
  Billing: AppRoute.billing,
  Funding: AppRoute.funding,
};

type RestrictRule = Array<SystemRole> | 'NON_AUTH' | 'ANY_ONE';

type RestrictRules = {
  navigation: RestrictRule;
  visibility: RestrictRule;
};

// eslint-disable-next-line consistent-return
const getRestrictRule = (r: AppRoute): RestrictRules => {
  switch (r) {
    case AppRoute.signIn:
    case AppRoute.signUp:
      return { navigation: 'NON_AUTH', visibility: 'NON_AUTH' };
    case AppRoute.notFound:
      return { navigation: 'ANY_ONE', visibility: 'ANY_ONE' };
    case AppRoute.signOut:
      return { navigation: 'ANY_ONE', visibility: [] };
    case AppRoute.settings:
    case AppRoute.settingsTeamBilling:
    case AppRoute.settingsTeamUsers:
    case AppRoute.settingsTeamProgramTypes:
    case AppRoute.settingsTeamIntegrations:
    case AppRoute.settingsPersonalProfile:
    case AppRoute.settingsPersonalIntegrations:
    case AppRoute.settingsPersonalNotifications:
    case AppRoute.home:
    case AppRoute.calendar:
    case AppRoute.businessPlan:
    case AppRoute.marketplace:
    case AppRoute.deeplink:
    case AppRoute.insights:
    case AppRoute.ai:
    case AppRoute.billing:
    case AppRoute.funding:
      return { navigation: [], visibility: [] };
  }
};

const isAvailable = (user: ICurrentUser | null, rule: RestrictRule): boolean => {
  switch (rule) {
    case 'ANY_ONE':
      return true;
    case 'NON_AUTH':
      return !user;
    default:
      return !!user && (rule.length === 0 || _.intersection(user.roles, rule).length > 0);
  }
};

export const isRouteAvailable = (user: ICurrentUser | null, route: AppRoute): boolean => {
  const { navigation } = getRestrictRule(route);
  return isAvailable(user, navigation);
};

export const isRouteVisible = (user: ICurrentUser | null, route: AppRoute): boolean => {
  const { visibility } = getRestrictRule(route);
  return isAvailable(user, visibility);
};

const getProgramDeepLinkRoute = (programId: string) =>
  AppRoutes.Program.deeplink.replace(':programId', programId);

const getProgramDeepLinkUrl = (programId: string) => {
  const route = getProgramDeepLinkRoute(programId);
  return `${window.location.origin}${route}`;
};

export const AppRoutesHelper = {
  getProgramDeepLinkUrl,
  getProgramDeepLinkRoute,
};
